import { useState, useEffect } from "react";
import { AdminButton } from "@/components/admin/button";
import { AdminIcon } from "@/components/admin/icon";
import { useToast } from "@/components/ui/feedback";
import { useCreateCustomer } from "@/lib/customers/customers-hooks";

interface AdminCustomerCreateModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: (customerId: string) => void;
}

export function AdminCustomerCreateModal({
	isOpen,
	onClose,
	onSuccess,
}: AdminCustomerCreateModalProps) {
	const toast = useToast();
	const create = useCreateCustomer();

	const [form, setForm] = useState({
		phone: "",
		firstName: "",
		lastName: "",
		email: "",
		acquisitionSource: "csr",
	});
	const [smsConsent, setSmsConsent] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Recommencer le formulaire quand la modale s'ouvre
	useEffect(() => {
		if (isOpen) {
			setForm({
				phone: "",
				firstName: "",
				lastName: "",
				email: "",
				acquisitionSource: "csr",
			});
			setSmsConsent(false);
			setError(null);
		}
	}, [isOpen]);

	// Fermeture sur la touche Échap
	useEffect(() => {
		if (!isOpen) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	const set =
		(k: keyof typeof form) =>
		(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
			setForm((f) => ({ ...f, [k]: e.target.value }));

	const submit = (e?: React.FormEvent) => {
		if (e) e.preventDefault();
		if (!form.phone.trim()) return;

		setError(null);
		create.mutate(
			{
				phone: form.phone.trim(),
				firstName: form.firstName.trim() || undefined,
				lastName: form.lastName.trim() || undefined,
				email: form.email.trim() || undefined,
				acquisitionSource: form.acquisitionSource,
				consents: [{ channel: "sms", granted: smsConsent }],
			},
			{
				onSuccess: (c) => {
					toast.success(`Client ${c.name || form.phone} créé avec succès.`);
					onClose();
					if (onSuccess) onSuccess(c.id);
				},
				onError: (err: unknown) => {
					const ax = err as { response?: { data?: { message?: string } } };
					const msg = ax.response?.data?.message ?? "Création impossible du client";
					setError(msg);
					toast.error(msg);
				},
			},
		);
	};

	return (
		<div
			role="dialog"
			aria-modal="true"
			style={{
				position: "fixed",
				inset: 0,
				zIndex: 1000,
				background: "rgba(20,16,12,0.45)",
				display: "grid",
				placeItems: "center",
				padding: 16,
			}}
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
		>
			<div
				style={{
					width: "min(520px, 100%)",
					background: "var(--a-surface, #fff)",
					border: "1px solid var(--a-border)",
					borderRadius: 12,
					padding: 24,
					display: "grid",
					gap: 16,
					boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
				}}
			>
				{/* En-tête de la modale */}
				<div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
					<div>
						<h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
							Nouveau client
						</h3>
						<p className="muted" style={{ fontSize: 13, margin: "4px 0 0 0" }}>
							Création manuelle d'un compte client par téléphone (CUS-03)
						</p>
					</div>
					<button
						type="button"
						className="clear-btn"
						onClick={onClose}
						style={{ padding: 4, cursor: "pointer", background: "none", border: "none" }}
						title="Fermer"
					>
						<AdminIcon n="close" s={18} />
					</button>
				</div>

				{/* Formulaire */}
				<form onSubmit={submit} style={{ display: "grid", gap: 14 }}>
					<Field label="Téléphone *">
						<input
							className="inp"
							placeholder="+237 6XX XX XX XX"
							value={form.phone}
							onChange={set("phone")}
							aria-label="Téléphone"
							autoFocus
						/>
					</Field>

					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
						<Field label="Prénom">
							<input
								className="inp"
								placeholder="Ex : Jean"
								value={form.firstName}
								onChange={set("firstName")}
								aria-label="Prénom"
							/>
						</Field>
						<Field label="Nom">
							<input
								className="inp"
								placeholder="Ex : Dupont"
								value={form.lastName}
								onChange={set("lastName")}
								aria-label="Nom"
							/>
						</Field>
					</div>

					<Field label="Email (optionnel)">
						<input
							className="inp"
							type="email"
							placeholder="jean.dupont@example.cm"
							value={form.email}
							onChange={set("email")}
							aria-label="Email"
						/>
					</Field>

					<Field label="Source d'acquisition">
						<select
							className="inp"
							value={form.acquisitionSource}
							onChange={set("acquisitionSource")}
							aria-label="Source"
						>
							<option value="csr">CSR (création manuelle)</option>
							<option value="organic">Organique</option>
							<option value="referral">Parrainage</option>
							<option value="paid_meta">Publicité Meta</option>
						</select>
					</Field>

					<label
						style={{
							display: "flex",
							gap: 8,
							alignItems: "center",
							fontSize: 13,
							cursor: "pointer",
							marginTop: 4,
						}}
					>
						<input
							type="checkbox"
							checked={smsConsent}
							onChange={(e) => setSmsConsent(e.target.checked)}
						/>
						Consentement SMS marketing (opt-in explicite, décoché par défaut)
					</label>

					{error && (
						<div
							style={{
								color: "var(--a-danger, #B23A2A)",
								fontSize: 13,
								padding: "8px 12px",
								background: "rgba(178, 58, 42, 0.08)",
								borderRadius: 6,
							}}
						>
							{error}
						</div>
					)}

					{/* Actions de bas de modale */}
					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 8,
							marginTop: 10,
							paddingTop: 12,
							borderTop: "1px solid var(--a-border)",
						}}
					>
						<AdminButton variant="ghost" type="button" onClick={onClose}>
							Annuler
						</AdminButton>
						<AdminButton
							variant="primary"
							type="submit"
							disabled={create.isPending || !form.phone.trim()}
						>
							{create.isPending ? "Création…" : "Créer le client"}
						</AdminButton>
					</div>
				</form>
			</div>
		</div>
	);
}

function Field({
	label,
	children,
}: {
	label: string;
	children: React.ReactNode;
}) {
	return (
		<label style={{ display: "grid", gap: 4 }}>
			<span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>
				{label}
			</span>
			{children}
		</label>
	);
}
